import { useQuery } from '@tanstack/react-query';

import { Chip } from '../../shared/ui/chip';
import { FilterBar } from '../../shared/ui/filter-bar';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../shared/ui/menu';
import { Segmented } from '../../shared/ui/segmented';
import { InfoDot } from '../../shared/ui/tooltip';
import { ChevronDownIcon } from '../../shared/ui/icons';
import { flowKeys, listFlows } from '../../entities/flow';
import { viewCount, type TIssueList } from '../../entities/issue';

// Open is only what nobody has looked at yet; Acknowledged is seen, not fixed; Handled is
// what someone resolved or ignored; Everything is all four.
export type TIssueTab = 'open' | 'acknowledged' | 'handled' | 'all';

export const VIEWS: { value: TIssueTab; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'handled', label: 'Handled' },
  { value: 'all', label: 'Everything' },
];

export const WINDOWS: { minutes: number; label: string }[] = [
  { minutes: 60, label: 'Last hour' },
  { minutes: 6 * 60, label: 'Last 6 hours' },
  { minutes: 24 * 60, label: 'Last 24 hours' },
  { minutes: 7 * 24 * 60, label: 'Last 7 days' },
  { minutes: 90 * 24 * 60, label: 'Everything we keep' },
];

const WIDEST = WINDOWS[WINDOWS.length - 1].minutes;

export interface IIssueFilters {
  view: TIssueTab;
  /** Issues last seen within this many minutes. */
  minutes: number;
  flowId?: string;
}

export const DEFAULT_ISSUE_FILTERS: IIssueFilters = { view: 'open', minutes: 24 * 60 };

// Old links said `view=active` (open + acknowledged, now two tabs) and any `minutes`; both
// still land on a tab and the nearest window rather than on nothing.
const TAB_OF: Record<string, TIssueTab> = {
  open: 'open',
  active: 'open',
  acknowledged: 'acknowledged',
  handled: 'handled',
  resolved: 'handled',
  ignored: 'handled',
  all: 'all',
};

export const parseIssueFilters = (search: Record<string, unknown>): IIssueFilters => {
  const minutes = Number(search.minutes);
  const window = Number.isFinite(minutes) && minutes > 0 ? minutes : undefined;
  return {
    view: TAB_OF[String(search.view)] ?? DEFAULT_ISSUE_FILTERS.view,
    minutes: window
      ? (WINDOWS.find((entry) => entry.minutes >= window)?.minutes ?? WIDEST)
      : DEFAULT_ISSUE_FILTERS.minutes,
    flowId: typeof search.flow === 'string' && search.flow ? search.flow : undefined,
  };
};

export const issueFiltersToSearch = (filters: IIssueFilters): Record<string, string | number> => ({
  ...(filters.view !== DEFAULT_ISSUE_FILTERS.view ? { view: filters.view } : {}),
  ...(filters.minutes !== DEFAULT_ISSUE_FILTERS.minutes ? { minutes: filters.minutes } : {}),
  ...(filters.flowId ? { flow: filters.flowId } : {}),
});

export const widestIssueWindow = WIDEST;

interface IIssueFilterBarProps {
  filters: IIssueFilters;
  onChange: (next: IIssueFilters) => void;
  counts?: TIssueList['counts'];
  note?: string;
}

export const IssueFilterBar = ({ filters, onChange, counts, note }: IIssueFilterBarProps) => {
  const flows = useQuery({ queryKey: flowKeys.list(), queryFn: () => listFlows() });
  const options = (flows.data?.data ?? [])
    .map((flow) => ({ id: flow.id, name: flow.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const picked = options.find((flow) => flow.id === filters.flowId);
  const window = WINDOWS.find((entry) => entry.minutes === filters.minutes) ?? WINDOWS[2];

  return (
    <FilterBar>
      <Segmented
        aria-label="Which issues"
        segments={VIEWS.map((view) => ({
          value: view.value,
          label: view.label,
          count: viewCount(counts, view.value),
        }))}
        value={filters.view}
        onChange={(value) => onChange({ ...filters, view: value as TIssueTab })}
      />

      {note && <InfoDot side="bottom">{note}</InfoDot>}

      <Menu>
        <MenuTrigger asChild>
          <Chip active={filters.minutes !== DEFAULT_ISSUE_FILTERS.minutes}>
            {window.label}
            <ChevronDownIcon size={13} />
          </Chip>
        </MenuTrigger>
        <MenuContent align="start">
          {WINDOWS.map((entry) => (
            <MenuItem
              key={entry.minutes}
              selected={entry.minutes === filters.minutes}
              onSelect={() => onChange({ ...filters, minutes: entry.minutes })}
            >
              {entry.label}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>

      <Menu>
        <MenuTrigger asChild>
          <Chip active={Boolean(filters.flowId)}>
            <span className="max-w-chip truncate">
              Flow: {filters.flowId ? (picked?.name ?? 'a deleted flow') : 'any'}
            </span>
            <ChevronDownIcon size={13} />
          </Chip>
        </MenuTrigger>
        <MenuContent align="start">
          <MenuItem
            selected={!filters.flowId}
            onSelect={() => onChange({ ...filters, flowId: undefined })}
          >
            Any flow
          </MenuItem>
          {options.length > 0 && <MenuSeparator />}
          {options.map((flow) => (
            <MenuItem
              key={flow.id}
              selected={flow.id === filters.flowId}
              onSelect={() => onChange({ ...filters, flowId: flow.id })}
            >
              {flow.name}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
    </FilterBar>
  );
};
